import type { Order, User, Menu, CartItem, ExtractedMenu, EmailLog } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

let getTokenFn: (() => Promise<string>) | null = null;

export function setTokenProvider(fn: () => Promise<string>): void {
  getTokenFn = fn;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  if (!getTokenFn) return {};
  const token = await getTokenFn();
  return { Authorization: `Bearer ${token}` };
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...options?.headers,
    },
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }

  return res.json();
}

export const api = {
  menus: {
    getWeek: (weekStart: string): Promise<Menu[]> =>
      apiFetch(`/menus?weekStart=${weekStart}`),

    ingest: (imageBase64: string, mimeType: string): Promise<ExtractedMenu> =>
      apiFetch('/menus/ingest', {
        method: 'POST',
        body: JSON.stringify({ imageBase64, mimeType }),
      }),

    save: (week: ExtractedMenu['week']): Promise<Menu[]> =>
      apiFetch('/menus/save', {
        method: 'POST',
        body: JSON.stringify({ week }),
      }),
  },

  orders: {
    getAll: (params?: { userId?: string; from?: string; to?: string }): Promise<Order[]> => {
      const qs = new URLSearchParams();
      if (params?.userId) qs.set('userId', params.userId);
      if (params?.from) qs.set('from', params.from);
      if (params?.to) qs.set('to', params.to);
      return apiFetch(`/orders${qs.toString() ? '?' + qs.toString() : ''}`);
    },

    create: (orders: Omit<CartItem, 'tempId'>[]): Promise<Order[]> =>
      apiFetch('/orders', {
        method: 'POST',
        body: JSON.stringify({ orders }),
      }),

    update: (id: string, data: Partial<Order>): Promise<Order> =>
      apiFetch(`/orders/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    cancel: (id: string): Promise<{ id: string; cancelled: boolean }> =>
      apiFetch(`/orders/${id}/cancel`, { method: 'PATCH' }),
  },

  users: {
    getAll: (): Promise<User[]> => apiFetch('/users'),
    getMe: (companyName?: string): Promise<User> =>
      apiFetch('/users/me', companyName ? { headers: { 'X-User-Company': companyName } } : undefined),
    updatePickupCode: (pickupCode: string): Promise<{ pickupCode: string }> =>
      apiFetch('/users/me/pickup-code', {
        method: 'PATCH',
        body: JSON.stringify({ pickupCode }),
      }),
    ensure: (user: { oid: string; firstName: string; lastName: string; email: string; company: string }): Promise<User> =>
      apiFetch('/users/ensure', {
        method: 'POST',
        body: JSON.stringify(user),
      }),
  },

  pickup: {
    validate: (code: string, dateISO: string): Promise<{
      found: boolean;
      alreadyRedeemed?: boolean;
      codeAssigned?: boolean;
      message?: string;
      orderId?: string;
      dish?: { name: string; allergens: string[] };
    }> =>
      apiFetch('/pickup/validate', {
        method: 'POST',
        body: JSON.stringify({ code, dateISO }),
      }),

    redeem: (code: string, dateISO: string): Promise<{ success: boolean; orderId: string }> =>
      apiFetch('/pickup/redeem', {
        method: 'POST',
        body: JSON.stringify({ code, dateISO }),
      }),
  },

  emailLogs: {
    getAll: (): Promise<EmailLog[]> => apiFetch('/email-logs'),
  },

  settings: {
    get: (): Promise<{ pickupEnabled: boolean }> => apiFetch('/settings'),
  },
};

export async function downloadExport(path: string): Promise<void> {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE}${path}`, { headers: headers as HeadersInit });
  if (!res.ok) throw new Error('Export fehlgeschlagen');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] || 'export.zip';
  a.click();
  URL.revokeObjectURL(url);
}
